import {
    AirspaceCheckCommand,
    AirspaceCheckResponse,
    AirspaceJurisdictionDto,
    CraftDto,
    InUseMissionWorkflowVersionDto,
    LocationDetailsDto,
    LookupObject,
    RpaTypeDto,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { MapSourceFilters } from '@flyfreely-portal-ui/map';
import { MissionFormValue } from 'libs/missions/src/lib/interfaces';
import moment from 'moment';
import { findLocationFlightAreaPolygon } from './airspace-check';

/**
 * Calculate the map source filters for the the airspace check response.
 * @param airspaceCheckResponse the response to use
 * @returns the source filter map
 */
export function mapSourceFiltersForResponse(
    airspaceCheckResponse: AirspaceCheckResponse
) {
    return airspaceCheckResponse.airspaceDatasetList?.reduce(
        (acc, v) => ({
            ...acc,
            [v.identifier]: v.version
        }),
        {} as MapSourceFilters
    );
}

export function buildAirspaceCheckCommand(
    rpas: CraftDto[],
    workflows: InUseMissionWorkflowVersionDto[],
    values: MissionFormValue,
    location: LocationDetailsDto,
    rpaTypes: LookupObject<RpaTypeDto>,
    overrideJurisdiction = false
): AirspaceCheckCommand | null {
    const {
        missionDate,
        missionEstimatedTime,
        timeZone,
        missionWorkflowVersionId,
        maximumHeight
    } = values;

    if (location == null) {
        return null;
    }

    const locationPolygon = findLocationFlightAreaPolygon(location.features);

    if (locationPolygon == null) {
        return null;
    }

    if (
        location.airspaceJurisdiction?.airspaceCheckSupport ===
            AirspaceJurisdictionDto.AirspaceCheckSupport.NONE &&
        !overrideJurisdiction
    ) {
        return null;
    }

    if (missionWorkflowVersionId == null) {
        return null;
    }

    const workflow = workflows.find(w => w.id === missionWorkflowVersionId);

    if (workflow == null) {
        return null;
    }

    if (workflow.delegatedAuthority == null) {
        return null;
    }

    const jurisdiction =
        workflow.delegatedAuthority.authorityType?.jurisdiction?.identifier;
    const ruleset =
        workflow.delegatedAuthority.authorityType?.ruleset?.identifier;

    if (jurisdiction == null) {
        return null;
    }

    if (ruleset == null) {
        return null;
    }

    if (missionDate == null || timeZone == null) {
        return null;
    }

    const startTime = toTimestamp(missionDate, timeZone);

    const endTime = moment(startTime)
        .add(missionEstimatedTime ?? 3600, 'seconds')
        .toISOString();

    return {
        startTime,
        endTime,
        location: locationPolygon,
        maximumHeight,
        jurisdiction,
        ruleset,
        rpaList: rpas.map(r => ({
            id: r.id,
            mtow: rpaTypes[r.rpaTypeId]?.performanceSpecifications
                ?.maxTakeOffWeight,
            serialNumber: r.manufacturerSerialNumber
        })),
        nearestFeatureSearch: false,
        nearestFeatureSearchRadiusMeters: 400
    } as AirspaceCheckCommand;
}
